/**
 * DataGateWarning Component
 * 
 * Shows a warning when Module 0 (Data Quality) hasn't passed
 * and prevents other modules from running without override.
 */

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DataGateStatus } from '@/lib/modules';

interface DataGateWarningProps {
  status: DataGateStatus;
  onOverride?: () => void;
  showInline?: boolean;
}

export function DataGateWarning({ status, onOverride, showInline = true }: DataGateWarningProps) {
  const { t } = useTranslation();

  if (status === 'passed') {
    return showInline ? (
      <Alert className="bg-success/10 border-success/20">
        <CheckCircle className="h-4 w-4 text-success" />
        <AlertTitle className="text-success">{t('modules.dataGate.passed')}</AlertTitle>
        <AlertDescription>
          Datakvalitetskontrollen är godkänd. Alla moduler kan köras.
        </AlertDescription>
      </Alert>
    ) : null;
  }

  if (status === 'passed_with_warnings') {
    return showInline ? (
      <Alert className="bg-warning/10 border-warning/20">
        <AlertTriangle className="h-4 w-4 text-warning" />
        <AlertTitle className="text-warning">{t('modules.dataGate.passedWithWarnings')}</AlertTitle>
        <AlertDescription>
          Datakvalitetskontrollen hittade mindre problem. Kontrollera varningarna innan du fortsätter.
        </AlertDescription>
      </Alert>
    ) : null;
  }

  if (status === 'failed') {
    return showInline ? (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <AlertTitle>{t('modules.dataGate.failed')}</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>Datakvalitetskontrollen misslyckades. Åtgärda problemen innan du kör andra moduler.</p>
          {onOverride && (
            <Button variant="outline" size="sm" onClick={onOverride}>
              {t('modules.dataGate.override')}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    ) : null;
  }

  // not_run
  return showInline ? (
    <Alert className="bg-muted/50 border-muted">
      <Clock className="h-4 w-4 text-muted-foreground" />
      <AlertTitle>{t('modules.dataGate.notRun')}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>Kör Modul 0 (Datakvalitet) först för att validera din data.</p>
        {onOverride && (
          <Button variant="outline" size="sm" onClick={onOverride}>
            {t('modules.dataGate.override')}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  ) : null;
}

interface DataGateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmOverride: () => void;
  status: DataGateStatus;
}

export function DataGateDialog({ open, onOpenChange, onConfirmOverride, status }: DataGateDialogProps) {
  const { t } = useTranslation();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            {t('modules.dataGate.required')}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              Modul 0 (Datakvalitet och fullständighet) har{' '}
              {status === 'not_run' ? 'inte körts ännu' : 'misslyckats'}.
            </p>
            <p>
              Det rekommenderas starkt att köra datakvalitetskontrollen först för att säkerställa
              att din data är komplett och korrekt innan analys.
            </p>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">
                Varning
              </Badge>
              <span className="text-sm">
                Att köra utan datakvalitetskontroll kan ge opålitliga resultat.
              </span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Avbryt</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirmOverride} className="bg-warning text-warning-foreground hover:bg-warning/90">
            {t('modules.dataGate.override')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function DataGateStatusBadge({ status }: { status: DataGateStatus }) {
  const { t } = useTranslation();

  const config = {
    not_run: { icon: Clock, color: 'bg-muted text-muted-foreground', label: t('modules.dataGate.notRun') },
    passed: { icon: CheckCircle, color: 'bg-success/10 text-success', label: t('modules.dataGate.passed') },
    passed_with_warnings: { icon: AlertTriangle, color: 'bg-warning/10 text-warning', label: t('modules.dataGate.passedWithWarnings') },
    failed: { icon: XCircle, color: 'bg-destructive/10 text-destructive', label: t('modules.dataGate.failed') },
  };

  const { icon: Icon, color, label } = config[status];

  return (
    <Badge variant="outline" className={color}>
      <Icon className="h-3 w-3 mr-1" />
      {label}
    </Badge>
  );
}
